import { useState } from 'react';
import { Star } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { Link } from 'react-router-dom';

const PricingToggle = ({ onChange }) => {
  const [isAnnual, setIsAnnual] = useState(true);
  const { t } = useTranslation();

  const handleToggle = (value) => {
    setIsAnnual(value);
    onChange(value);
  };

  return (
    <div className="inline-flex p-1 bg-gray-100 rounded-full">
      <button
        className={`relative px-6 py-2 text-sm font-medium transition-all duration-200 rounded-full ${
          !isAnnual
            ? 'text-primary-500 bg-white shadow-sm'
            : 'text-gray-600 hover:text-gray-900'
        }`}
        onClick={() => handleToggle(false)}
      >
        {t('monthly')}
      </button>
      <button
        className={`relative px-6 py-2 text-sm font-medium transition-all duration-200 rounded-full ${
          isAnnual
            ? 'text-primary-500 bg-white shadow-sm'
            : 'text-gray-600 hover:text-gray-900'
        }`}
        onClick={() => handleToggle(true)}
      >
        {t('annual')}{' '}
        <span className={isAnnual ? 'text-primary-500' : 'text-gray-600'}>
          {t('annualDiscount')}
        </span>
      </button>
    </div>
  );
};

const PricingCard = ({
  title,
  price,
  isAnnual,
  customerLimit,
  features,
  isPopular,
}) => {
  const finalPrice = isAnnual ? Math.floor(price * 0.75) : price;
  const { t } = useTranslation();

  return (
    <div
      className={`relative rounded-xl border p-6 ${
        isPopular ? 'border-primary-500 shadow-lg' : 'border-gray-200'
      }`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <div className="bg-primary-500 text-white px-3 py-1 text-xs rounded-full">
            {t('mostPopular')}
          </div>
        </div>
      )}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
        <select className="w-full border border-gray-200 rounded-lg p-2 mb-4 text-sm">
          <option value={customerLimit}>{customerLimit}</option>
        </select>
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold">€{finalPrice}</span>
          <span className="text-gray-500 text-sm">{t('perMonth')}</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">{t('priceDisclaimer')}</p>
      </div>
      <ul className="space-y-3 mb-6">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center gap-2 text-sm">
            <svg
              className="h-4 w-4 text-primary-500"
              fill="none"
              strokeWidth="2"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span>{t(feature)}</span>
          </li>
        ))}
      </ul>
      <Link to="/access">
        <button
          className={`w-full py-2 text-center text-sm font-medium rounded-lg border ${
            isPopular
              ? 'bg-primary-500 text-white border-primary-500 hover:bg-primary-500'
              : 'border-gray-200 hover:border-primary-500 hover:text-primary-500'
          }`}
        >
          {t('tryFreeDays')}
        </button>
      </Link>
    </div>
  );
};

const Price = () => {
  const [isAnnual, setIsAnnual] = useState(true);
  const { t } = useTranslation();

  return (
    <section id="pricing" className="py-16 px-4">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <div className="inline-block bg-gray-100 rounded-full px-4 py-1 text-sm mb-4">
            {t('pricing')}
          </div>
          <h2 className="text-3xl font-bold mb-4">{t('pricingTitle')}</h2>
          <p className="text-gray-500 max-w-2xl mx-auto mb-8">
            {t('pricingDescription')}
          </p>
          <PricingToggle onChange={setIsAnnual} />
        </div>

        <div className="flex items-center justify-center gap-4 mb-12">
          <div className="flex -space-x-2">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="w-8 h-8 rounded-full border-2 border-white bg-gray-200"
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="w-4 h-4 text-yellow-400 fill-current"
                />
              ))}
            </div>
            <p className="text-sm text-gray-500">{t('trustedProviders')}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <PricingCard
            title="Basic"
            price={49}
            isAnnual={isAnnual}
            customerLimit={t('upToPatients').replace('${count}', '100')}
            features={[
              'appointmentScheduling',
              'patientRecords',
              'emailReminders',
              'basicReporting',
              'emailSupport',
            ]}
          />
          <PricingCard
            title="Professional"
            price={99}
            isAnnual={isAnnual}
            customerLimit={t('upToPatients').replace('${count}', '500')}
            features={[
              'everythingInBasic',
              'customBranding',
              'advancedAnalytics',
              'smsReminders',
              'prioritySupport',
              'teamManagement',
            ]}
            isPopular
          />
          <PricingCard
            title="Enterprise"
            price={199}
            isAnnual={isAnnual}
            customerLimit={t('unlimitedPatients')}
            features={[
              'everythingInProfessional',
              'customIntegrations',
              'dedicatedManager',
              'slaGuarantee',
              'hipaaCompliance',
              'apiAccess',
            ]}
          />
        </div>

        <div className="flex items-center justify-center gap-2 mt-6 text-xs text-gray-500">
          <span>•</span>
          <span>{t('tryWithoutLimits')}</span>
          <span>•</span>
          <span>{t('noCreditCardRequired')}</span>
          <span>•</span>
          <span>{t('cancelAtAnyTime')}</span>
        </div>
      </div>
    </section>
  );
};

export default Price;
